import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "../../lib/i18n";

// ---------------------------------------------------------------------------
// AutoTranslator — invisible component that walks the DOM after every route
// change and swaps every English text node to Hindi using the backend's
// cached translation service.
//
// Design goals:
//   • ZERO CODE CHANGES to existing components — we translate DOM text
//     nodes, not React children.
//   • Cheap: unique strings sent once, cached per-session + persistently
//     in MongoDB.
//   • Resilient: React re-renders repaint English text nodes → the
//     MutationObserver picks them up and re-translates on the fly.
//   • Non-blocking: translations arrive asynchronously; content is
//     visible in English first, then swapped in place (< 500 ms usually).
//
// Selectors we deliberately DO NOT translate:
//   • <script>, <style>, <noscript>
//   • Elements with data-notranslate  (opt-out)
//   • Elements with lang="hi-*"       (already Hindi in source)
//   • Text inside <input>/<textarea> value attributes (form state)
// ---------------------------------------------------------------------------

const SKIP_TAGS = new Set([
  "SCRIPT", "STYLE", "NOSCRIPT", "SVG", "PATH", "CIRCLE",
  "IFRAME", "CODE", "PRE",
]);

const ALREADY_HINDI_RE = /[\u0900-\u097F]/;

const isTranslatable = (node) => {
  if (!node || node.nodeType !== Node.TEXT_NODE) return false;
  const p = node.parentElement;
  if (!p) return false;
  if (SKIP_TAGS.has(p.tagName)) return false;
  if (p.closest("[data-notranslate]")) return false;
  const lang = (p.closest("[lang]")?.getAttribute("lang") || "").toLowerCase();
  if (lang.startsWith("hi")) return false;
  const raw = node.nodeValue || "";
  const stripped = raw.trim();
  if (stripped.length < 2) return false;
  // If it already contains Devanagari, assume translated / native Hindi.
  if (ALREADY_HINDI_RE.test(stripped)) return false;
  // Skip pure numbers / prices / URLs / emails — server also filters but
  // shortcut here saves a round-trip.
  if (/^[\d\s+\-().,/₹$€£:@]+$/.test(stripped)) return false;
  if (/^https?:\/\//.test(stripped)) return false;
  if (/^[\w.\-+]+@[\w-]+\.[\w.\-]+$/.test(stripped)) return false;
  return true;
};

const collectTextNodes = (root) => {
  const out = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => (isTranslatable(n) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT),
  });
  let n;
  while ((n = walker.nextNode())) out.push(n);
  return out;
};

const AutoTranslator = () => {
  const { lang, translate } = useLang();
  const location = useLocation();
  const observerRef = useRef(null);
  const inFlightRef = useRef(false);
  const pendingRef = useRef(new Set());
  const timerRef = useRef(null);

  useEffect(() => {
    // If English, disconnect any observer and let React repaint English
    // content naturally.  We deliberately do NOT undo prior translations
    // — the next hard-refresh restores English cleanly; a soft toggle
    // trades a small flash of Hindi text for zero extra complexity.
    if (lang !== "hi") {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      return;
    }

    const root = document.querySelector("main") || document.body;
    if (!root) return;

    const scheduleFlush = () => {
      if (timerRef.current) return;
      timerRef.current = setTimeout(async () => {
        timerRef.current = null;
        if (inFlightRef.current) { scheduleFlush(); return; }
        const nodes = Array.from(pendingRef.current);
        pendingRef.current.clear();
        if (!nodes.length) return;
        inFlightRef.current = true;
        try {
          // Each node -> its trimmed source; we translate the trimmed
          // form so leading/trailing whitespace stays untouched.
          const sources = nodes.map((n) => (n.nodeValue || "").trim());
          const translated = await translate(sources);
          nodes.forEach((n, i) => {
            const src = n.nodeValue || "";
            const dst = translated[i];
            if (!dst || dst === sources[i]) return;
            // Preserve leading/trailing whitespace so layout doesn't shift.
            const lead = src.match(/^\s*/)?.[0] || "";
            const trail = src.match(/\s*$/)?.[0] || "";
            n.nodeValue = `${lead}${dst}${trail}`;
          });
        } finally {
          inFlightRef.current = false;
        }
      }, 120); // debounce so React finishing a burst of renders sends one batch
    };

    const enqueue = (node) => {
      if (isTranslatable(node)) pendingRef.current.add(node);
    };

    // 1) Initial sweep — the current DOM.
    collectTextNodes(root).forEach((n) => pendingRef.current.add(n));
    scheduleFlush();

    // 2) React re-renders + route transitions — MutationObserver.
    const obs = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "characterData") {
          enqueue(m.target);
        } else if (m.type === "childList") {
          m.addedNodes.forEach((added) => {
            if (added.nodeType === Node.TEXT_NODE) {
              enqueue(added);
            } else if (added.nodeType === Node.ELEMENT_NODE) {
              collectTextNodes(added).forEach((n) => pendingRef.current.add(n));
            }
          });
        }
      }
      if (pendingRef.current.size) scheduleFlush();
    });
    obs.observe(root, {
      subtree: true, childList: true, characterData: true,
    });
    observerRef.current = obs;

    return () => {
      obs.disconnect();
      observerRef.current = null;
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    };
  }, [lang, location.pathname, translate]);

  return null;
};

export default AutoTranslator;
